import { useEffect } from "react";

/**
 * Robust scroll-reveal.
 * Adds `.is-visible` to every `.tt-reveal` element as it enters the viewport
 * using a native IntersectionObserver. Includes hard failsafes so content is
 * NEVER left permanently hidden (reduced-motion, no IO support, or a late
 * safety timeout all reveal everything).
 */
export function useReveal() {
  useEffect(() => {
    const revealAll = () => {
      document
        .querySelectorAll(".tt-reveal")
        .forEach((el) => el.classList.add("is-visible"));
    };

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      revealAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.06 }
    );

    const elements = Array.from(document.querySelectorAll(".tt-reveal"));
    elements.forEach((el) => observer.observe(el));

    // Failsafe: if anything is still hidden after 4s (e.g. observer edge cases),
    // force-reveal so the page can never appear empty.
    const failsafe = window.setTimeout(revealAll, 4000);

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);
}
